import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../data/products';
import { useReveal } from '../../hooks/useReveal';
import './ProductIndex.scss';

const StatusTag = ({ status }) => {
    const live = status === 'LIVE';
    return (
        <span className={`pi-status ${live ? 'is-live' : ''}`}>
            <span className="pi-status-dot" aria-hidden="true" />
            {status}
        </span>
    );
};

const ProductRow = ({ product, index, onEnter, onLeave, active }) => {
    const [ref, revealed] = useReveal();
    const num = String(index + 1).padStart(2, '0');
    const label = product.short || product.name;

    const body = (
        <>
            <div className="pi-row-meta">
                <span className="pi-row-num">{num}</span>
                <span className="pi-row-cat">{product.category}</span>
            </div>

            <div className="pi-row-main">
                <h3 className="pi-row-name">{label}</h3>
                <p className="pi-row-summary">{product.summary}</p>
            </div>

            <div className="pi-row-side">
                <StatusTag status={product.status} />
                <span className="pi-row-action">
                    {product.href ? (
                        <>
                            {product.external ? `Explore ${label}` : `Explore ${label}`}
                            <span className="pi-row-arrow" aria-hidden="true">→</span>
                        </>
                    ) : (
                        <span className="pi-row-locked">In the lab</span>
                    )}
                </span>
            </div>
        </>
    );

    const className = `pi-row ${revealed ? 'is-in' : ''} ${active ? 'is-active' : ''} ${
        product.href ? '' : 'is-static'
    }`;

    const shared = {
        className,
        style: { '--accent': product.accent, '--d': `${(index % 4) * 70}ms` },
        onMouseEnter: () => onEnter(product.id),
        onMouseLeave: onLeave,
        onFocus: () => onEnter(product.id),
        onBlur: onLeave,
    };

    return (
        <div ref={ref} className="pi-row-wrap">
            {!product.href && <div {...shared}>{body}</div>}
            {product.href && product.external && (
                <a {...shared} href={product.href} target="_blank" rel="noopener noreferrer">
                    {body}
                </a>
            )}
            {/*
              `standalone` = same origin, but a separate app rather than a
              route in this one (Evolved UI at /ui). It needs a real navigation
              in the same tab; a router Link would be handled client-side and
              never load it.
            */}
            {product.href && !product.external && product.standalone && (
                <a {...shared} href={product.href}>
                    {body}
                </a>
            )}
            {product.href && !product.external && !product.standalone && (
                <Link {...shared} to={product.href}>
                    {body}
                </Link>
            )}
        </div>
    );
};

const ProductIndex = () => {
    const [active, setActive] = useState(null);
    const [headRef, headIn] = useReveal();

    return (
        <section className="product-index" id="products">
            <div className={`pi-head ${headIn ? 'is-in' : ''}`} ref={headRef}>
                <span className="pi-eyebrow">Products</span>
                <h2 className="pi-title">
                    Ideas become experiments.
                    <br />
                    <span className="pi-title-dim">Experiments become products.</span>
                </h2>
            </div>

            <div
                className={`pi-list ${active ? 'has-active' : ''}`}
                onMouseLeave={() => setActive(null)}
            >
                {products.map((product, i) => (
                    <ProductRow
                        key={product.id}
                        product={product}
                        index={i}
                        active={active === product.id}
                        onEnter={setActive}
                        onLeave={() => setActive(null)}
                    />
                ))}
            </div>
        </section>
    );
};

export default ProductIndex;
